// src/ValidatorDashboard.js
import React, { useState, useEffect } from "react";
import { useAuth } from "./AuthContext";

export default function ValidatorDashboard() {
  const { user, logout } = useAuth();
  const [farmers, setFarmers] = useState([]);
  const [samples, setSamples] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Fetch all farmers and samples
      const [farmersRes, samplesRes] = await Promise.all([
        fetch('/api/farmers/all', {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch('/api/mrv_samples', {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      const farmersData = await farmersRes.json();
      const samplesData = await samplesRes.json();

      setFarmers(farmersData.rows || []);
      setSamples(samplesData.rows || []);
      setError("");
    } catch (err) {
      setError("Failed to load data: " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const verifyFarmer = async (farmerId, approved) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/farmers/${farmerId}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          verified: approved ? 1 : 0,
          verified_by: user.email,
          verified_at: new Date().toISOString()
        })
      });

      if (!response.ok) throw new Error('Verification failed');
      
      alert(approved ? 'Farmer approved!' : 'Farmer rejected');
      fetchData(); // Refresh data
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const verifySample = async (sampleId, approved) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/mrv_samples/${sampleId}/verify`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          verified: approved ? 1 : 0,
          verified_by: user.email,
          verified_at: new Date().toISOString()
        })
      });

      if (!response.ok) throw new Error('Verification failed');
      
      alert(approved ? 'Sample approved!' : 'Sample rejected');
      fetchData(); // Refresh data
    } catch (err) {
      alert('Error: ' + err.message);
    }
  };

  const unverifiedFarmers = farmers.filter(f => !f.verified);
  const unverifiedSamples = samples.filter(s => !s.verified);

  return (
    <div style={{
      minHeight: '100vh',
      background: '#0f2419',
      color: 'white',
      padding: '20px'
    }}>
      {/* Header */}
      <header style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px',
        paddingBottom: '20px',
        borderBottom: '1px solid #2d5a45'
      }}>
        <div>
          <h1 style={{ margin: 0 }}>Validator Dashboard</h1>
          <p style={{ margin: '8px 0 0 0', color: '#888' }}>
            Logged in as: {user?.email} ({user?.role})
          </p>
        </div>
        <button
          onClick={logout}
          style={{
            padding: '10px 20px',
            background: '#ef4444',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold'
          }}
        >
          Logout
        </button>
      </header>

      {error && (
        <div style={{
          background: '#fee2e2',
          color: '#991b1b',
          padding: '12px',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Loading...</div>
      ) : (
        <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
          
          {/* Summary Cards */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '16px',
            marginBottom: '40px'
          }}>
            <SummaryCard title="Pending Farmers" value={unverifiedFarmers.length} color="#f59e0b" />
            <SummaryCard title="Pending Samples" value={unverifiedSamples.length} color="#f59e0b" />
            <SummaryCard title="Total Farmers" value={farmers.length} color="#4ade80" />
            <SummaryCard title="Total Samples" value={samples.length} color="#4ade80" />
          </div>

          {/* Unverified Farmers */}
          <section style={{ marginBottom: '40px' }}>
            <h2 style={{ marginBottom: '16px' }}>
              Pending Farmer Verifications ({unverifiedFarmers.length})
            </h2>
            {unverifiedFarmers.length === 0 ? (
              <p style={{ color: '#888' }}>No pending farmer verifications</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#1a3327',
                  borderRadius: '8px'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2d5a45' }}>
                      <th style={thStyle}>Name</th>
                      <th style={thStyle}>Email</th>
                      <th style={thStyle}>Country</th>
                      <th style={thStyle}>Region</th>
                      <th style={thStyle}>District</th>
                      <th style={thStyle}>Created</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unverifiedFarmers.map(farmer => (
                      <tr key={farmer.farmer_id} style={{ borderBottom: '1px solid #2d5a45' }}>
                        <td style={tdStyle}>{farmer.full_name}</td>
                        <td style={tdStyle}>{farmer.email}</td>
                        <td style={tdStyle}>{farmer.country}</td>
                        <td style={tdStyle}>{farmer.region}</td>
                        <td style={tdStyle}>{farmer.district}</td>
                        <td style={tdStyle}>{new Date(farmer.created_at).toLocaleDateString()}</td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => verifyFarmer(farmer.farmer_id, true)}
                            style={{
                              padding: '6px 12px',
                              background: '#4ade80',
                              color: '#0f2419',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '8px',
                              fontWeight: 'bold'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => verifyFarmer(farmer.farmer_id, false)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Unverified Samples */}
          <section>
            <h2 style={{ marginBottom: '16px' }}>
              Pending Sample Verifications ({unverifiedSamples.length})
            </h2>
            {unverifiedSamples.length === 0 ? (
              <p style={{ color: '#888' }}>No pending sample verifications</p>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  background: '#1a3327',
                  borderRadius: '8px'
                }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2d5a45' }}>
                      <th style={thStyle}>Sample ID</th>
                      <th style={thStyle}>Plot ID</th>
                      <th style={thStyle}>Date</th>
                      <th style={thStyle}>Depth (cm)</th>
                      <th style={thStyle}>SOC %</th>
                      <th style={thStyle}>pH</th>
                      <th style={thStyle}>Created By</th>
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {unverifiedSamples.map(sample => (
                      <tr key={sample.sample_id} style={{ borderBottom: '1px solid #2d5a45' }}>
                        <td style={tdStyle}>{sample.sample_id}</td>
                        <td style={tdStyle}>{sample.plot_id}</td>
                        <td style={tdStyle}>{sample.sample_date}</td>
                        <td style={tdStyle}>{sample.sample_depth_cm}</td>
                        <td style={tdStyle}>{sample.soil_organic_carbon_pct}</td>
                        <td style={tdStyle}>{sample.ph}</td>
                        <td style={tdStyle}>{sample.created_by}</td>
                        <td style={tdStyle}>
                          <button
                            onClick={() => verifySample(sample.sample_id, true)}
                            style={{
                              padding: '6px 12px',
                              background: '#4ade80',
                              color: '#0f2419',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              marginRight: '8px',
                              fontWeight: 'bold'
                            }}
                          >
                            Approve
                          </button>
                          <button
                            onClick={() => verifySample(sample.sample_id, false)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontWeight: 'bold'
                            }}
                          >
                            Reject
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function SummaryCard({ title, value, color }) {
  return (
    <div style={{
      background: '#1a3327',
      padding: '20px',
      borderRadius: '8px',
      border: '1px solid #2d5a45'
    }}>
      <div style={{ fontSize: '14px', color: '#888', marginBottom: '8px' }}>{title}</div>
      <div style={{ fontSize: '32px', fontWeight: 'bold', color }}>{value}</div>
    </div>
  );
}

const thStyle = {
  padding: '12px',
  textAlign: 'left',
  color: '#4ade80',
  fontWeight: 'bold'
};

const tdStyle = {
  padding: '12px',
  color: '#ccc'
};